//! Filter dropdown buttons on a pivot report's field headers.
//!
//! Excel draws a small dropdown button on "Row Labels" / "Column Labels" (and
//! on each field header in outline and tabular form) that opens the field's
//! sort-and-filter menu. Core says which header cells carry one
//! (`JsPivotLayout.field_buttons`); this draws the button at the right edge of
//! each cell and reports hits so the pointer layer can open the menu.

import type { Table } from '@/app/gridGL/cells/tables/Table';
import type { TablePointerDownResult } from '@/app/gridGL/cells/tables/Tables';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { getCSSVariableTint } from '@/app/helpers/convertColor';
import type { JsPivotFieldButton } from '@/app/quadratic-core-types';
import type { Point } from 'pixi.js';
import { Container, Graphics, Rectangle } from 'pixi.js';

// Side of the square button. Sized to sit inside a default-height cell with a
// little air above and below.
const BUTTON_SIZE = 13;

// Gap between the button and the right edge of the cell.
const BUTTON_MARGIN = 2;

// Half-width of the caret inside the button.
const CARET_SIZE = 3;

interface Control {
  button: JsPivotFieldButton;
  /** World coordinates, matching how the other table hit regions are tested. */
  bounds: Rectangle;
}

export class TablePivotFieldControls extends Container {
  private table: Table;
  private graphics: Graphics;
  private controls: Control[] = [];

  tableCursor: string | undefined;

  constructor(table: Table) {
    super();
    this.table = table;
    this.graphics = this.addChild(new Graphics());
  }

  update = () => {
    this.graphics.clear();
    this.controls = [];

    const pivot = this.table.codeCell.pivot;
    if (!pivot || this.table.codeCell.spill_error) {
      return;
    }

    const border = getCSSVariableTint('border');
    const background = getCSSVariableTint('background');
    const caret = getCSSVariableTint('muted-foreground');
    // An active filter tints the caret, standing in for Excel's funnel icon.
    const filteredCaret = getCSSVariableTint('primary');
    for (const button of pivot.field_buttons) {
      const cell = this.table.sheet.getCellOffsets(Number(button.x), Number(button.y));
      const size = Math.min(BUTTON_SIZE, cell.height - 2);
      const left = cell.x + cell.width - size - BUTTON_MARGIN;
      const top = cell.y + (cell.height - size) / 2;

      this.drawButton(left, top, size, border, background, button.filtered ? filteredCaret : caret);
      this.controls.push({
        button,
        bounds: new Rectangle(left, top, size, size),
      });
    }
  };

  private drawButton = (x: number, y: number, size: number, border: number, background: number, caret: number) => {
    const local = { x: x - this.table.tableBounds.x, y: y - this.table.tableBounds.y };
    this.graphics.lineStyle(1, border, 1);
    this.graphics.beginFill(background);
    this.graphics.drawRoundedRect(local.x, local.y, size, size, 2);
    this.graphics.endFill();

    const centerX = local.x + size / 2;
    const centerY = local.y + size / 2;
    this.graphics.lineStyle();
    this.graphics.beginFill(caret);
    this.graphics.drawPolygon([
      centerX - CARET_SIZE,
      centerY - CARET_SIZE / 2,
      centerX + CARET_SIZE,
      centerY - CARET_SIZE / 2,
      centerX,
      centerY + CARET_SIZE,
    ]);
    this.graphics.endFill();
  };

  pointerMove = (world: Point): boolean => {
    const over = this.controls.some((control) => intersects.rectanglePoint(control.bounds, world));
    this.tableCursor = over ? 'pointer' : undefined;
    return over;
  };

  pointerDown = (world: Point): TablePointerDownResult | undefined => {
    const control = this.controls.find((control) => intersects.rectanglePoint(control.bounds, world));
    if (!control) return;
    return { table: this.table.codeCell, type: 'pivot-field', pivotField: control.button };
  };
}
